import React, { useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  Search, 
  User, 
  Users, 
  Gamepad2, 
  Trophy, 
  Video, 
  Image, 
  Globe, 
  Activity,
  Loader2,
  CheckCircle2,
  XCircle,
  Clock,
  MapPin,
  Star,
  Shield,
  Calendar,
  Copy,
  UserPlus,
  UserCheck,
  BadgeCheck,
  Tv,
  Wifi,
  Building2,
  Map
} from 'lucide-react';
import { xboxApiService, ResolverResult } from '@/services/xboxApi';

// XSS-safe text sanitization - strips HTML tags and limits length
const sanitizeText = (text: string | undefined | null, maxLength = 500): string => {
  if (!text) return '';
  // Remove any HTML tags
  const stripped = text.replace(/<[^>]*>/g, '');
  // Remove control characters except newlines and tabs (using character codes)
  // eslint-disable-next-line no-control-regex
  const controlCharRegex = new RegExp('[\\x00-\\x08\\x0B\\x0C\\x0E-\\x1F\\x7F]', 'g');
  const cleaned = stripped.replace(controlCharRegex, '');
  // Limit length
  return cleaned.slice(0, maxLength);
};

// Validate URL is safe (only https from trusted Xbox domains)
const isValidXboxUrl = (url: string | undefined | null): boolean => {
  if (!url) return false;
  try {
    const parsed = new URL(url);
    // Only allow https
    if (parsed.protocol !== 'https:') return false;
    // Only allow known Xbox/Microsoft domains
    const trustedDomains = [
      'xboxlive.com',
      'xbox.com',
      'microsoft.com',
      'live.com',
      'xboxcdn.com',
      'xboxservices.com',
      'xboxcontent.com',
    ];
    const hostname = parsed.hostname.toLowerCase();
    return trustedDomains.some(domain => 
      hostname === domain || hostname.endsWith('.' + domain)
    );
  } catch {
    return false;
  }
};

// Get safe URL or return undefined
const getSafeUrl = (url: string | undefined | null): string | undefined => {
  if (!url) return undefined;
  return isValidXboxUrl(url) ? url : undefined;
};

interface ProfileData {
  xuid: string;
  gamertag: string;
  gamerscore: number;
  accountTier: string;
  reputation: string;
  bio: string;
  location: string;
  tenureLevel: number;
  avatarUrl: string;
  watermarks: string[];
  realName: string;
}

interface PresenceData {
  state: string;
  lastSeen?: {
    timestamp: string;
    deviceType: string;
    titleName: string;
  };
  devices?: Array<{
    type: string;
    titles: Array<{
      name: string;
      state: string;
      richPresence?: string;
    }>;
  }>;
}

interface LoadingState {
  profile: 'idle' | 'loading' | 'success' | 'error';
  social: 'idle' | 'loading' | 'success' | 'error';
  resolver: 'idle' | 'loading' | 'success' | 'error';
  presence: 'idle' | 'loading' | 'success' | 'error';
  friends: 'idle' | 'loading' | 'success' | 'error';
  friendsResolve: 'idle' | 'loading' | 'success' | 'error';
  titles: 'idle' | 'loading' | 'success' | 'error';
  achievements: 'idle' | 'loading' | 'success' | 'error';
  clips: 'idle' | 'loading' | 'success' | 'error';
  screenshots: 'idle' | 'loading' | 'success' | 'error';
  clubs: 'idle' | 'loading' | 'success' | 'error';
  activity: 'idle' | 'loading' | 'success' | 'error';
}

interface SocialData {
  followerCount: number;
  followingCount: number;
  hasGamePass: boolean;
  isVerified: boolean;
  isBroadcasting: boolean;
  isXbox360Gamerpic: boolean;
  lastSeenDateTimeUtc?: string;
  presenceText?: string;
}

interface ResolvedFriend {
  xuid: string;
  gamertag: string;
  avatarUrl?: string;
  gamerscore?: number;
}

const ProfileInspector: React.FC = () => {
  const [searchInput, setSearchInput] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [social, setSocial] = useState<SocialData | null>(null);
  const [resolver, setResolver] = useState<ResolverResult | null>(null);
  const [presence, setPresence] = useState<PresenceData | null>(null);
  const [friends, setFriends] = useState<any>(null);
  const [titles, setTitles] = useState<any>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [achievements, setAchievements] = useState<any>(null);
  const [clips, setClips] = useState<any>(null);
  const [screenshots, setScreenshots] = useState<any>(null);
  const [clubs, setClubs] = useState<any>(null);
  const [activity, setActivity] = useState<any>(null);
  const [resolvedFriends, setResolvedFriends] = useState<ResolvedFriend[]>([]);
  
  const [loadingState, setLoadingState] = useState<LoadingState>({
    profile: 'idle',
    social: 'idle',
    resolver: 'idle',
    presence: 'idle',
    friends: 'idle',
    friendsResolve: 'idle',
    titles: 'idle',
    achievements: 'idle',
    clips: 'idle',
    screenshots: 'idle',
    clubs: 'idle',
    activity: 'idle',
  });

  const resetState = () => {
    setProfile(null);
    setSocial(null);
    setResolver(null);
    setPresence(null);
    setFriends(null);
    setResolvedFriends([]);
    setTitles(null);
    setAchievements(null);
    setClips(null);
    setScreenshots(null);
    setClubs(null);
    setActivity(null);
    setSearchError(null);
    setLoadingState({
      profile: 'idle',
      social: 'idle',
      resolver: 'idle',
      presence: 'idle',
      friends: 'idle',
      friendsResolve: 'idle',
      titles: 'idle',
      achievements: 'idle',
      clips: 'idle',
      screenshots: 'idle',
      clubs: 'idle',
      activity: 'idle',
    });
  };

  // Resolve XUIDs to gamertags in batches (respecting rate limits with retry)
  const resolveFriendsXuids = useCallback(async (xuids: string[]) => {
    if (xuids.length === 0) return;
    
    setLoadingState(prev => ({ ...prev, friendsResolve: 'loading' }));
    
    const resolved: ResolvedFriend[] = [];
    const BATCH_SIZE = 30; // Conservative batch size to avoid rate limits
    const BASE_DELAY = 1500; // 1.5 second base delay between batches
    const MAX_RETRIES = 3;
    const RATE_LIMIT_WAIT = 20000; // 20 seconds wait on rate limit
    
    // Use refs to track mutable state across iterations
    const state = {
      consecutiveErrors: 0,
      currentDelay: BASE_DELAY,
    };
    
    // Helper to delay
    const delay = (ms: number) => new Promise(r => setTimeout(r, ms));
    
    // Process a single batch with retries
    const processBatch = async (batch: string[], batchIndex: number): Promise<void> => {
      let retries = 0;
      
      while (retries < MAX_RETRIES) {
        try {
          const result = await xboxApiService.resolveXuidsBatch(
            batch, 
            ['Gamertag', 'GameDisplayPicRaw', 'Gamerscore']
          );
          
          // Check for rate limit error in response
          if (result.error) {
            const errorLower = result.error.toLowerCase();
            if (errorLower.includes('429') || errorLower.includes('rate') || errorLower.includes('too many')) {
              console.warn(`Rate limited on batch ${batchIndex + 1}, waiting ${RATE_LIMIT_WAIT / 1000}s...`);
              await delay(RATE_LIMIT_WAIT);
              retries++;
              state.consecutiveErrors++;
              // Increase delay for future batches
              state.currentDelay = Math.min(state.currentDelay * 1.5, 10000);
              continue;
            }
            // Other error - log and continue
            console.warn(`Error resolving batch: ${result.error}`);
            retries++;
            state.consecutiveErrors++;
            await delay(2000);
            continue;
          }
          
          if (result.data?.profileUsers) {
            for (const user of result.data.profileUsers) {
              const settings = user.settings || [];
              const getSetting = (id: string) => settings.find((s: { id: string; value: string }) => s.id === id)?.value || '';
              
              resolved.push({
                xuid: user.id,
                gamertag: getSetting('Gamertag') || user.id,
                avatarUrl: getSetting('GameDisplayPicRaw'),
                gamerscore: parseInt(getSetting('Gamerscore')) || 0,
              });
            }
            
            // Update state incrementally so UI shows progress
            setResolvedFriends([...resolved]);
            state.consecutiveErrors = 0;
            // Gradually reduce delay on success
            state.currentDelay = Math.max(state.currentDelay * 0.9, BASE_DELAY);
          }
          // Success - exit retry loop
          return;
        } catch (err) {
          console.error('Failed to resolve batch:', err);
          retries++;
          state.consecutiveErrors++;
          
          // Exponential backoff on repeated errors
          const backoffDelay = Math.min(2000 * Math.pow(2, retries), 30000);
          await delay(backoffDelay);
        }
      }
    };
    
    // Process all batches
    const batches: string[][] = [];
    for (let i = 0; i < xuids.length; i += BATCH_SIZE) {
      batches.push(xuids.slice(i, i + BATCH_SIZE));
    }
    
    for (let i = 0; i < batches.length; i++) {
      await processBatch(batches[i], i);
      
      // If we've had too many consecutive errors, slow down significantly
      if (state.consecutiveErrors >= 3) {
        console.warn('Multiple consecutive errors, increasing delay...');
        state.currentDelay = Math.min(state.currentDelay * 2, 15000);
        state.consecutiveErrors = 0; // Reset counter but keep higher delay
      }
      
      // Delay between batches to respect rate limits
      if (i < batches.length - 1) {
        await delay(state.currentDelay);
      }
    }
    
    setLoadingState(prev => ({ ...prev, friendsResolve: resolved.length > 0 ? 'success' : 'error' }));
  }, []);

  const parseProfile = (data: any): ProfileData | null => {
    const user = data?.profileUsers?.[0];
    if (!user) return null;
    
    const settings = user.settings || [];
    const getSetting = (id: string) => settings.find((s: any) => s.id === id)?.value || '';
    
    return {
      xuid: user.id || user.hostId,
      gamertag: getSetting('Gamertag'),
      gamerscore: parseInt(getSetting('Gamerscore')) || 0,
      accountTier: getSetting('AccountTier'),
      reputation: getSetting('XboxOneRep'),
      bio: getSetting('Bio'),
      location: getSetting('Location'),
      tenureLevel: parseInt(getSetting('TenureLevel')) || 0,
      avatarUrl: getSetting('GameDisplayPicRaw'),
      watermarks: getSetting('Watermarks')?.split('|').filter(Boolean) || [],
      realName: getSetting('RealName'),
    };
  };

  const handleSearch = useCallback(async () => {
    if (!searchInput.trim()) return;
    
    resetState();
    setIsSearching(true);
    
    // Step 1: Lookup profile first
    setLoadingState(prev => ({ ...prev, profile: 'loading' }));
    
    const profileResult = await xboxApiService.profileLookup(searchInput.trim());
    
    if (profileResult.error || !profileResult.data) {
      setSearchError(profileResult.error || 'Profile not found');
      setLoadingState(prev => ({ ...prev, profile: 'error' }));
      setIsSearching(false);
      return;
    }
    
    const parsedProfile = parseProfile(profileResult.data);
    if (!parsedProfile) {
      setSearchError('Could not parse profile data');
      setLoadingState(prev => ({ ...prev, profile: 'error' }));
      setIsSearching(false);
      return;
    }
    
    setProfile(parsedProfile);
    setLoadingState(prev => ({ ...prev, profile: 'success' }));
    
    const xuid = parsedProfile.xuid;
    
    // Step 2: Fire all other APIs in parallel
    setLoadingState(prev => ({
      ...prev,
      social: 'loading',
      resolver: 'loading',
      presence: 'loading',
      friends: 'loading',
      titles: 'loading',
      achievements: 'loading',
      clips: 'loading',
      screenshots: 'loading',
      clubs: 'loading',
      activity: 'loading',
    }));
    
    // Resolver (IP lookup via Octolus API + GeoIP)
    xboxApiService.resolveGamertag(parsedProfile.gamertag).then(res => {
      if (res.data && !res.error) {
        setResolver(res.data);
        setLoadingState(prev => ({ ...prev, resolver: 'success' }));
      } else {
        setLoadingState(prev => ({ ...prev, resolver: 'error' }));
      }
    });
    
    // Social (PeopleHub - follower/following counts, hasGamePass, etc.)
    xboxApiService.profileSocial(xuid).then(res => {
      if (res.data?.people?.[0] && !res.error) {
        const person = res.data.people[0];
        const detail = person.detail || {};
        setSocial({
          followerCount: detail.followerCount || 0,
          followingCount: detail.followingCount || 0,
          hasGamePass: detail.hasGamePass || false,
          isVerified: detail.isVerified || false,
          isBroadcasting: person.isBroadcasting || false,
          isXbox360Gamerpic: person.isXbox360Gamerpic || false,
          lastSeenDateTimeUtc: person.lastSeenDateTimeUtc,
          presenceText: person.presenceText,
        });
        setLoadingState(prev => ({ ...prev, social: 'success' }));
      } else {
        setLoadingState(prev => ({ ...prev, social: 'error' }));
      }
    });
    
    // Presence
    xboxApiService.profilePresence(xuid).then(res => {
      if (res.data && !res.error) {
        setPresence(res.data);
        setLoadingState(prev => ({ ...prev, presence: 'success' }));
      } else {
        setLoadingState(prev => ({ ...prev, presence: 'error' }));
      }
    });
    
    // Friends - then resolve XUIDs to gamertags
    xboxApiService.profileFriends(xuid).then(async res => {
      if (res.data && !res.error) {
        setFriends(res.data);
        setLoadingState(prev => ({ ...prev, friends: 'success' }));
        
        // Extract XUIDs and resolve them
        const friendXuids = res.data.people?.map((p: any) => p.xuid).filter(Boolean) || [];
        if (friendXuids.length > 0) {
          // Limit to first 100 friends to avoid too many API calls
          resolveFriendsXuids(friendXuids.slice(0, 100));
        }
      } else {
        setLoadingState(prev => ({ ...prev, friends: 'error' }));
      }
    });
    
    // Titles
    xboxApiService.profileTitles(xuid).then(res => {
      if (res.data && !res.error) {
        setTitles(res.data);
        setLoadingState(prev => ({ ...prev, titles: 'success' }));
      } else {
        setLoadingState(prev => ({ ...prev, titles: 'error' }));
      }
    });
    
    // Achievements
    xboxApiService.profileAchievements(xuid).then(res => {
      if (res.data && !res.error) {
        setAchievements(res.data);
        setLoadingState(prev => ({ ...prev, achievements: 'success' }));
      } else {
        setLoadingState(prev => ({ ...prev, achievements: 'error' }));
      }
    });
    
    // Clips
    xboxApiService.profileClips(xuid).then(res => {
      if (res.data && !res.error) {
        setClips(res.data);
        setLoadingState(prev => ({ ...prev, clips: 'success' }));
      } else {
        setLoadingState(prev => ({ ...prev, clips: 'error' }));
      }
    });
    
    // Screenshots
    xboxApiService.profileScreenshots(xuid).then(res => {
      if (res.data && !res.error) {
        setScreenshots(res.data);
        setLoadingState(prev => ({ ...prev, screenshots: 'success' }));
      } else {
        setLoadingState(prev => ({ ...prev, screenshots: 'error' }));
      }
    });
    
    // Clubs
    xboxApiService.profileClubs(xuid).then(res => {
      if (res.data && !res.error) {
        setClubs(res.data);
        setLoadingState(prev => ({ ...prev, clubs: 'success' }));
      } else {
        setLoadingState(prev => ({ ...prev, clubs: 'error' }));
      }
    });
    
    // Activity
    xboxApiService.profileActivity(xuid).then(res => {
      if (res.data && !res.error) {
        setActivity(res.data);
        setLoadingState(prev => ({ ...prev, activity: 'success' }));
      } else {
        setLoadingState(prev => ({ ...prev, activity: 'error' }));
      }
    });
    
    setIsSearching(false);
  }, [searchInput, resolveFriendsXuids]);

  const StatusIcon = ({ status }: { status: 'idle' | 'loading' | 'success' | 'error' }) => {
    switch (status) {
      case 'loading':
        return <Loader2 className="h-4 w-4 animate-spin text-blue-400" />;
      case 'success':
        return <CheckCircle2 className="h-4 w-4 text-green-400" />;
      case 'error':
        return <XCircle className="h-4 w-4 text-red-400" />;
      default:
        return <div className="h-4 w-4" />;
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const formatDate = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleString();
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="space-y-6">
      {/* Search Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search className="h-5 w-5" />
            Profile Inspector
          </CardTitle>
          <CardDescription>
            Enter a Gamertag or XUID to lookup player information
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-3 sm:flex-row sm:gap-4">
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Enter Gamertag or XUID..."
              className="flex-1 px-4 py-2 rounded-md border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <Button className="w-full sm:w-auto"
              onClick={handleSearch} 
              disabled={isSearching || !searchInput.trim()}
            >
              {isSearching ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Search className="h-4 w-4 mr-2" />
              )}
              Search
            </Button>
          </div>
          
          {searchError && (
            <div className="mt-4 p-4 rounded-md bg-red-500/10 border border-red-500/20 text-red-400">
              {searchError}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Loading Status Grid */}
      {(loadingState.profile !== 'idle' || profile) && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">API Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 md:grid-cols-5 md:gap-4">
              {Object.entries(loadingState).map(([key, status]) => (
                <div key={key} className="flex items-center gap-2 text-sm">
                  <StatusIcon status={status} />
                  <span className="capitalize">{key}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Profile Card */}
      {profile && (
        <Card>
          <CardHeader>
            <div className="flex flex-col items-start gap-4 sm:flex-row">
              {getSafeUrl(profile.avatarUrl) && (
                <img 
                  src={getSafeUrl(profile.avatarUrl)} 
                  alt={sanitizeText(profile.gamertag, 50)}
                  className="h-20 w-20 shrink-0 rounded-lg"
                  loading="lazy"
                  decoding="async"
                />
              )}
              <div className="min-w-0 flex-1">
                <CardTitle className="flex flex-wrap items-center gap-2 break-words">
                  <User className="h-5 w-5" />
                  {sanitizeText(profile.gamertag, 50)}
                  {profile.realName && (
                    <span className="text-sm font-normal text-muted-foreground">
                      ({sanitizeText(profile.realName, 50)})
                    </span>
                  )}
                </CardTitle>
                <CardDescription className="mt-1">
                  <div className="flex flex-wrap items-center gap-4 text-sm">
                    <span 
                      className="flex max-w-full cursor-pointer items-center gap-1 break-all hover:text-primary"
                      onClick={() => copyToClipboard(profile.xuid)}
                      title="Click to copy XUID"
                    >
                      <Copy className="h-3 w-3" />
                      XUID: {profile.xuid}
                    </span>
                    <span className="flex items-center gap-1">
                      <Star className="h-3 w-3 text-yellow-400" />
                      {profile.gamerscore.toLocaleString()} G
                    </span>
                    <span className="flex items-center gap-1">
                      <Shield className="h-3 w-3" />
                      {profile.accountTier}
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {profile.tenureLevel > 0 ? `${profile.tenureLevel} year${profile.tenureLevel > 1 ? 's' : ''}` : '< 1 year'}
                    </span>
                    {profile.location && (
                      <span className="flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        {sanitizeText(profile.location, 100)}
                      </span>
                    )}
                  </div>
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Account Details Grid */}
            <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-4 md:gap-4">
              <div className="p-3 rounded-md bg-muted/50">
                <div className="text-xs text-muted-foreground mb-1">Gamerscore</div>
                <div className="font-semibold text-yellow-400 flex items-center gap-1">
                  <Star className="h-4 w-4" />
                  {profile.gamerscore.toLocaleString()}
                </div>
              </div>
              <div className="p-3 rounded-md bg-muted/50">
                <div className="text-xs text-muted-foreground mb-1">Account Type</div>
                <div className="font-semibold flex items-center gap-1">
                  <Shield className="h-4 w-4" />
                  {profile.accountTier || 'Unknown'}
                  {social?.hasGamePass && (
                    <span className="ml-1 px-1.5 py-0.5 text-xs bg-green-500/20 text-green-400 rounded">
                      Game Pass
                    </span>
                  )}
                </div>
              </div>
              <div className="p-3 rounded-md bg-muted/50">
                <div className="text-xs text-muted-foreground mb-1">Reputation</div>
                <div className={`font-semibold ${
                  profile.reputation === 'GoodPlayer' ? 'text-green-400' :
                  profile.reputation === 'NeedsWork' ? 'text-yellow-400' :
                  profile.reputation === 'AvoidMe' ? 'text-red-400' : ''
                }`}>
                  {profile.reputation || 'Unknown'}
                </div>
              </div>
              <div className="p-3 rounded-md bg-muted/50">
                <div className="text-xs text-muted-foreground mb-1">Account Age</div>
                <div className="font-semibold flex items-center gap-1">
                  <Calendar className="h-4 w-4" />
                  {profile.tenureLevel > 0 ? `${profile.tenureLevel} year${profile.tenureLevel > 1 ? 's' : ''}` : '< 1 year'}
                </div>
              </div>
            </div>

            {/* Social Stats (from PeopleHub) */}
            {social && (
              <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-4 md:gap-4">
                <div className="p-3 rounded-md bg-muted/50">
                  <div className="text-xs text-muted-foreground mb-1">Followers</div>
                  <div className="font-semibold flex items-center gap-1">
                    <UserCheck className="h-4 w-4" />
                    {social.followerCount.toLocaleString()}
                  </div>
                </div>
                <div className="p-3 rounded-md bg-muted/50">
                  <div className="text-xs text-muted-foreground mb-1">Following</div>
                  <div className="font-semibold flex items-center gap-1">
                    <UserPlus className="h-4 w-4" />
                    {social.followingCount.toLocaleString()}
                  </div>
                </div>
                {social.isVerified && (
                  <div className="p-3 rounded-md bg-blue-500/10 border border-blue-500/20">
                    <div className="text-xs text-muted-foreground mb-1">Status</div>
                    <div className="font-semibold flex items-center gap-1 text-blue-400">
                      <BadgeCheck className="h-4 w-4" />
                      Verified
                    </div>
                  </div>
                )}
                {social.isBroadcasting && (
                  <div className="p-3 rounded-md bg-purple-500/10 border border-purple-500/20">
                    <div className="text-xs text-muted-foreground mb-1">Status</div>
                    <div className="font-semibold flex items-center gap-1 text-purple-400">
                      <Tv className="h-4 w-4" />
                      Live Now
                    </div>
                  </div>
                )}
                {social.lastSeenDateTimeUtc && (
                  <div className="p-3 rounded-md bg-muted/50 md:col-span-2">
                    <div className="text-xs text-muted-foreground mb-1">Last Seen</div>
                    <div className="font-semibold flex items-center gap-1 text-sm">
                      <Clock className="h-4 w-4" />
                      {new Date(social.lastSeenDateTimeUtc).toLocaleString()}
                    </div>
                    {social.presenceText && (
                      <div className="text-xs text-muted-foreground mt-1">{social.presenceText}</div>
                    )}
                  </div>
                )}
              </div>
            )}
            
            {profile.bio && (
              <div className="mb-4 p-3 rounded-md bg-muted/50">
                <div className="text-xs text-muted-foreground mb-1">Bio</div>
                <p className="text-sm whitespace-pre-wrap break-words">{sanitizeText(profile.bio, 1000)}</p>
              </div>
            )}
            {profile.watermarks.length > 0 && (
              <div>
                <div className="text-xs text-muted-foreground mb-2">Badges & Achievements</div>
                <div className="flex flex-wrap gap-2">
                  {profile.watermarks.slice(0, 10).map((badge, i) => (
                    <span 
                      key={i}
                      className="px-2 py-1 text-xs rounded-full bg-primary/10 text-primary"
                    >
                      {sanitizeText(badge, 50)}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* OctoSniff Lookup Card (IP + GeoIP) */}
      {resolver && (
        <Card className={resolver.resolved ? 'border-orange-500/30' : ''}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Wifi className="h-5 w-5 text-orange-500" />
              OctoSniff Lookup
              {loadingState.resolver === 'loading' && (
                <Loader2 className="h-4 w-4 animate-spin ml-2" />
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {resolver.resolved ? (
              <div className="space-y-4">
                {/* IP Address - Prominent Display */}
                <div className="p-4 rounded-lg bg-orange-500/10 border border-orange-500/20">
                  <div className="text-xs text-muted-foreground mb-1">IP Address</div>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xl font-bold text-orange-400">{resolver.ip}</span>
                    <button 
                      onClick={() => copyToClipboard(resolver.ip || '')}
                      className="p-1 rounded hover:bg-muted"
                      title="Copy IP"
                    >
                      <Copy className="h-4 w-4 text-muted-foreground" />
                    </button>
                  </div>
                  {resolver.lookupDate && (
                    <div className="text-xs text-muted-foreground mt-1">
                      Lookup: {new Date(resolver.lookupDate).toLocaleString()}
                    </div>
                  )}
                </div>
                
                {/* GeoIP Data */}
                {resolver.geo && (
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-4">
                    {resolver.geo.country && (
                      <div className="p-3 rounded-md bg-muted/50">
                        <div className="text-xs text-muted-foreground mb-1">Country</div>
                        <div className="font-semibold flex items-center gap-1">
                          <Globe className="h-4 w-4" />
                          {resolver.geo.country}
                          {resolver.geo.countryCode && (
                            <span className="text-xs text-muted-foreground">({resolver.geo.countryCode})</span>
                          )}
                        </div>
                      </div>
                    )}
                    {resolver.geo.city && (
                      <div className="p-3 rounded-md bg-muted/50">
                        <div className="text-xs text-muted-foreground mb-1">City</div>
                        <div className="font-semibold flex items-center gap-1">
                          <MapPin className="h-4 w-4" />
                          {resolver.geo.city}
                          {resolver.geo.region && (
                            <span className="text-xs text-muted-foreground">, {resolver.geo.region}</span>
                          )}
                        </div>
                      </div>
                    )}
                    {resolver.geo.isp && (
                      <div className="p-3 rounded-md bg-muted/50">
                        <div className="text-xs text-muted-foreground mb-1">ISP</div>
                        <div className="font-semibold flex items-center gap-1 text-sm">
                          <Building2 className="h-4 w-4" />
                          <span className="truncate">{resolver.geo.isp}</span>
                        </div>
                      </div>
                    )}
                    {resolver.geo.timezone && (
                      <div className="p-3 rounded-md bg-muted/50">
                        <div className="text-xs text-muted-foreground mb-1">Timezone</div>
                        <div className="font-semibold flex items-center gap-1 text-sm">
                          <Clock className="h-4 w-4" />
                          {resolver.geo.timezone}
                        </div>
                      </div>
                    )}
                    {resolver.geo.org && (
                      <div className="p-3 rounded-md bg-muted/50 md:col-span-2">
                        <div className="text-xs text-muted-foreground mb-1">Organization</div>
                        <div className="font-semibold text-sm">{resolver.geo.org}</div>
                      </div>
                    )}
                    {(resolver.geo.latitude !== undefined && resolver.geo.longitude !== undefined) && (
                      <div className="p-3 rounded-md bg-muted/50 md:col-span-2">
                        <div className="text-xs text-muted-foreground mb-1">Coordinates</div>
                        <div className="font-semibold flex items-center gap-1 text-sm">
                          <Map className="h-4 w-4" />
                          {resolver.geo.latitude.toFixed(4)}, {resolver.geo.longitude.toFixed(4)}
                          <a 
                            href={`https://www.google.com/maps?q=${resolver.geo.latitude},${resolver.geo.longitude}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="ml-2 text-xs text-primary hover:underline"
                          >
                            View Map
                          </a>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="p-4 rounded-lg bg-muted/50 text-center">
                <div className="text-muted-foreground">
                  {resolver.message || 'No IP data available for this gamertag'}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Presence Card */}
      {presence && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Activity className="h-5 w-5" />
              Online Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  presence.state === 'Online' 
                    ? 'bg-green-500/20 text-green-400' 
                    : presence.state === 'Away'
                    ? 'bg-yellow-500/20 text-yellow-400'
                    : 'bg-gray-500/20 text-gray-400'
                }`}>
                  {presence.state}
                </span>
              </div>
              
              {presence.devices && presence.devices.length > 0 && (
                <div className="space-y-2">
                  {presence.devices.map((device, i) => (
                    <div key={i} className="p-3 rounded-md bg-muted/50">
                      <div className="text-sm font-medium">{sanitizeText(device.type, 50)}</div>
                      {device.titles.map((title, j) => (
                        <div key={j} className="text-sm text-muted-foreground">
                          Playing: {sanitizeText(title.name, 100)}
                          {title.richPresence && ` - ${sanitizeText(title.richPresence, 200)}`}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
              
              {presence.lastSeen && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Clock className="h-4 w-4" />
                  Last seen: {formatDate(presence.lastSeen.timestamp)} on {sanitizeText(presence.lastSeen.deviceType, 50)}
                  {presence.lastSeen.titleName && ` - ${sanitizeText(presence.lastSeen.titleName, 100)}`}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Friends Card */}
      {friends && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Users className="h-5 w-5" />
              Friends ({friends.totalCount || friends.people?.length || 0})
              {loadingState.friendsResolve === 'loading' && (
                <span className="text-xs font-normal text-muted-foreground flex items-center gap-1">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Resolving {resolvedFriends.length}/{Math.min(friends.people?.length || 0, 100)}
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {friends.people && friends.people.length > 0 ? (
              <div className="grid max-h-80 grid-cols-1 gap-2 overflow-y-auto sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                {resolvedFriends.length > 0 ? (
                  // Show resolved friends with gamertags and avatars
                  resolvedFriends.map((friend, i) => (
                    <div 
                      key={i}
                      className="flex items-center gap-2 p-2 rounded-md bg-muted/50 cursor-pointer hover:bg-muted transition-colors"
                      onClick={() => {
                        setSearchInput(friend.gamertag || friend.xuid);
                      }}
                      title={`XUID: ${friend.xuid}`}
                    >
                      {getSafeUrl(friend.avatarUrl) ? (
                        <img 
                          src={getSafeUrl(friend.avatarUrl)} 
                          alt={sanitizeText(friend.gamertag, 50)}
                          className="w-8 h-8 rounded"
                          loading="lazy"
                          decoding="async"
                        />
                      ) : (
                        <div className="w-8 h-8 rounded bg-muted flex items-center justify-center">
                          <User className="h-4 w-4 text-muted-foreground" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{sanitizeText(friend.gamertag, 50)}</div>
                        {friend.gamerscore !== undefined && friend.gamerscore > 0 && (
                          <div className="text-xs text-muted-foreground flex items-center gap-1">
                            <Star className="h-3 w-3 text-yellow-400" />
                            {friend.gamerscore.toLocaleString()}
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  // Fallback to XUIDs while resolving
                  friends.people.slice(0, 50).map((friend: any, i: number) => (
                    <div 
                      key={i}
                      className="flex items-center gap-2 p-2 rounded-md bg-muted/50 text-sm cursor-pointer hover:bg-muted"
                      onClick={() => {
                        setSearchInput(friend.xuid);
                      }}
                    >
                      <div className="w-8 h-8 rounded bg-muted/50 flex items-center justify-center">
                        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                      </div>
                      <span className="truncate text-muted-foreground">{friend.xuid}</span>
                    </div>
                  ))
                )}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No friends visible (may be private)</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Games Card */}
      {titles && titles.titles && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Gamepad2 className="h-5 w-5" />
              Games Played ({titles.titles.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {titles.titles.slice(0, 30).map((game: any, i: number) => (
                <div key={i} className="flex items-center gap-3 p-2 rounded-md bg-muted/50">
                  {getSafeUrl(game.displayImage) && (
                    <img 
                      src={getSafeUrl(game.displayImage)} 
                      alt={sanitizeText(game.name, 100)} 
                      className="w-12 h-12 rounded object-cover" 
                      loading="lazy"
                      decoding="async"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">{sanitizeText(game.name, 100)}</div>
                    <div className="text-xs text-muted-foreground">
                      {game.achievement?.currentGamerscore || 0}/{game.achievement?.totalGamerscore || 0} G
                      {game.titleHistory?.lastTimePlayed && (
                        <span className="ml-2">
                          Last: {formatDate(game.titleHistory.lastTimePlayed).split(',')[0]}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Game Clips */}
      {clips && clips.gameClips && clips.gameClips.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Video className="h-5 w-5" />
              Game Clips ({clips.gameClips.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid max-h-60 grid-cols-1 gap-4 overflow-y-auto sm:grid-cols-2 md:grid-cols-4">
              {clips.gameClips.slice(0, 12).map((clip: any, i: number) => (
                <div key={i} className="space-y-1">
                  {getSafeUrl(clip.thumbnails?.[0]?.uri) && (
                    getSafeUrl(clip.gameClipUris?.[0]?.uri) ? (
                      <a href={getSafeUrl(clip.gameClipUris?.[0]?.uri)} target="_blank" rel="noopener noreferrer">
                        <img 
                          src={getSafeUrl(clip.thumbnails[0].uri)} 
                          alt={sanitizeText(clip.clipName, 50) || 'Clip'} 
                          className="w-full h-20 object-cover rounded"
                          loading="lazy"
                          decoding="async"
                        />
                      </a>
                    ) : (
                      <img 
                        src={getSafeUrl(clip.thumbnails[0].uri)} 
                        alt={sanitizeText(clip.clipName, 50) || 'Clip'} 
                        className="w-full h-20 object-cover rounded"
                        loading="lazy"
                        decoding="async"
                      />
                    )
                  )}
                  <div className="text-xs truncate">{sanitizeText(clip.titleName, 100)}</div>
                  <div className="text-xs text-muted-foreground">{typeof clip.views === 'number' ? clip.views : 0} views</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Screenshots */}
      {screenshots && screenshots.screenshots && screenshots.screenshots.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Image className="h-5 w-5" />
              Screenshots ({screenshots.screenshots.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid max-h-60 grid-cols-1 gap-4 overflow-y-auto sm:grid-cols-2 md:grid-cols-4">
              {screenshots.screenshots.slice(0, 12).map((ss: any, i: number) => (
                <div key={i} className="space-y-1">
                  {getSafeUrl(ss.thumbnails?.[0]?.uri) && (
                    getSafeUrl(ss.screenshotUris?.[0]?.uri) ? (
                      <a href={getSafeUrl(ss.screenshotUris?.[0]?.uri)} target="_blank" rel="noopener noreferrer">
                        <img 
                          src={getSafeUrl(ss.thumbnails[0].uri)} 
                          alt={sanitizeText(ss.userCaption, 100) || 'Screenshot'} 
                          className="w-full h-20 object-cover rounded"
                          loading="lazy"
                          decoding="async"
                        />
                      </a>
                    ) : (
                      <img 
                        src={getSafeUrl(ss.thumbnails[0].uri)} 
                        alt={sanitizeText(ss.userCaption, 100) || 'Screenshot'} 
                        className="w-full h-20 object-cover rounded"
                        loading="lazy"
                        decoding="async"
                      />
                    )
                  )}
                  <div className="text-xs truncate">{sanitizeText(ss.titleName, 100)}</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Clubs */}
      {clubs && clubs.clubs && clubs.clubs.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Globe className="h-5 w-5" />
              Clubs ({clubs.clubs.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {clubs.clubs.slice(0, 10).map((club: any, i: number) => (
                <div key={i} className="flex items-center gap-3 p-2 rounded-md bg-muted/50">
                  {getSafeUrl(club.displayImageUrl) && (
                    <img 
                      src={getSafeUrl(club.displayImageUrl)} 
                      alt={sanitizeText(club.profile?.name?.value, 100)} 
                      className="w-10 h-10 rounded"
                      loading="lazy"
                      decoding="async"
                    />
                  )}
                  <div>
                    <div className="font-medium text-sm">{sanitizeText(club.profile?.name?.value, 100)}</div>
                    <div className="text-xs text-muted-foreground">
                      {typeof club.membersCount === 'number' ? club.membersCount : 0} members
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Activity Feed */}
      {activity && activity.activityItems && activity.activityItems.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Trophy className="h-5 w-5" />
              Recent Activity
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {activity.activityItems.slice(0, 15).map((item: any, i: number) => (
                <div key={i} className="flex items-start gap-3 p-2 rounded-md bg-muted/50">
                  <div className="flex-1">
                    <div className="text-sm">
                      <span className="font-medium">[{sanitizeText(item.activityItemType, 50)}]</span>
                      {' '}{sanitizeText(item.contentTitle, 100)}
                    </div>
                    {item.description && (
                      <div className="text-xs text-muted-foreground">{sanitizeText(item.description, 200)}</div>
                    )}
                    <div className="text-xs text-muted-foreground mt-1">
                      {formatDate(item.date)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ProfileInspector;
